// Global Instructions Rule Applied!
//
// Reporting → Period Highlights (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
// Phase 1, owner decision 2): the review surface for the versioned fund /
// company highlight sets. Chrome copies the newest ViewToolbar list siblings
// (internal_agents/index.tsx: filters in beforeAddSlot, ribbon CTA via
// RIBBON_CTA_CLASS) with the table inside a FormSection (CommitmentsTab
// precedent). A set opens as a form tab (the distribution-lists idiom).
import React, { useCallback, useState } from 'react'
import { Button, Modal, Select, Switch, Table, Tag, Tooltip } from 'antd'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import ViewToolbar, { RIBBON_CTA_CLASS } from '../../../core/components/ViewToolbar/index.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type { CoverageNote, HighlightSetRecord } from '../../../state_manager/data_models/period_highlights_model.ts'
import { usePeriodHighlights } from '../hooks/usePeriodHighlights.ts'
import { generateHighlights, GenerateHighlightsPayload } from '../services/periodHighlightsService.ts'
import { GenerateHighlightsModal } from './GenerateHighlightsModal.tsx'

export const STATUS_TAG: Record<string, { color: string; label: string }> = {
  draft: { color: 'blue', label: 'Draft' },
  // Live (2026-09-07): the open period's accumulating set (version 0) —
  // the followers and trackers keep it up to date; never rendered.
  live: { color: 'blue', label: 'Live' },
  approved: { color: 'green', label: 'Approved' },
  superseded: { color: 'default', label: 'Superseded' },
  rejected: { color: 'red', label: 'Rejected' }
}

const RUN_TAG: Record<string, { color: string; label: string }> = {
  queued: { color: 'blue', label: 'Queued' },
  dispatched: { color: 'blue', label: 'Queued' },
  processing: { color: 'blue', label: 'Generating' },
  done: { color: 'green', label: 'Generated' },
  error: { color: 'red', label: 'Error' },
  unconfigured: { color: 'red', label: 'Unconfigured' },
  skipped: { color: 'default', label: 'Skipped' }
}

/** The version Tag every highlights surface shares (docs/FRONTEND_DESIGN_PATTERNS.md
 *  "Figure tokens in agent prose"): green `v{n} current` for the version the
 *  reports render, blue `v0 live` for the open period's accumulating set,
 *  blue draft, red rejected, grey otherwise. "current" replaces the earlier
 *  "live" wording so a live set cannot be mistaken for the rendered one. */
export const versionTag = (r: { highlight_sets__version: number; highlight_sets__status: string; highlight_sets__is_current: boolean }): { color: string; label: string } => {
  if (r.highlight_sets__is_current) return { color: 'green', label: `v${r.highlight_sets__version} current` }
  const color = r.highlight_sets__status === 'live' || r.highlight_sets__status === 'draft' ? 'blue'
    : r.highlight_sets__status === 'rejected' ? 'red' : 'default'
  return { color, label: `v${r.highlight_sets__version} ${r.highlight_sets__status}` }
}

/** One sentence on who a finalised fund / LP set went ahead without
 *  (params.coverage.unavailable / wait_expired, 2026-09-09): the Coverage
 *  tooltips on the list and the form. */
export const describeCoverageGaps = (cov: CoverageNote): string => {
  const missing = (cov.missing || []).length
  const unavailable = cov.unavailable ?? []
  const count = (kind: string) => unavailable.filter((u) => u.kind === kind).length
  const parts: string[] = []
  const paused = count('paused')
  if (paused) parts.push(`${paused} follower${paused === 1 ? '' : 's'} paused (exited or dead compan${paused === 1 ? 'y' : 'ies'})`)
  const skipped = count('skipped')
  if (skipped) parts.push(`${skipped} atom run${skipped === 1 ? '' : 's'} skipped`)
  const failed = count('failed')
  if (failed) parts.push(`${failed} atom run${failed === 1 ? '' : 's'} failed`)
  const draft = count('draft')
  if (draft) parts.push(`${draft} atom${draft === 1 ? '' : 's'} awaiting approval`)
  const none = count('no_atom')
  if (none) parts.push(`${none} follower${none === 1 ? '' : 's'} wrote no atom`)
  const timedOut = (cov.wait_expired ?? []).length
  if (timedOut) parts.push(`${timedOut} still generating when the wait ran out`)
  return `${missing} portfolio compan${missing === 1 ? 'y' : 'ies'} had no approved atom when this set was generated`
    + (parts.length ? `: ${parts.join(', ')}` : '')
    + '. Open the set to run the missing atoms, then Reformulate.'
}

/** The Generate payload that finalises a set's key (scope + period). */
export const finalisePayload = (r: { highlight_sets__scope_type: string; highlight_sets__fund_id?: string | null; highlight_sets__fund_group_id?: string | null; highlight_sets__account_id?: string | null; highlight_sets__period_start: string; highlight_sets__period_end: string }): GenerateHighlightsPayload => ({
  scope_type: r.highlight_sets__scope_type as GenerateHighlightsPayload['scope_type'],
  ...(r.highlight_sets__scope_type === 'fund' ? { fund_id: r.highlight_sets__fund_id ?? undefined }
    : r.highlight_sets__scope_type === 'fund_group' ? { fund_group_id: r.highlight_sets__fund_group_id ?? undefined }
      : { account_id: r.highlight_sets__account_id ?? undefined }),
  period_start: r.highlight_sets__period_start,
  period_end: r.highlight_sets__period_end,
  force: false
})

const SCOPE_LABEL: Record<string, string> = {
  fund: 'Fund', fund_group: 'Consolidated fund', lp_account: 'LP', company: 'Company'
}

const token = () => getAccessToken(store.getState().auth)

export const PeriodHighlightsPage: React.FC = () => {
  const { openFormInTab } = useTabManagerContext()
  const {
    rows, count, loading, filters, setFilters, sorting, setSorting,
    page, setPage, pageSize, setPageSize, options, refetch, refetchOptions
  } = usePeriodHighlights()
  const [generateOpen, setGenerateOpen] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [modal, contextHolder] = Modal.useModal()
  // Bulk history (past quarters, fleet or one subject) is an admin operation
  // managed only from Configuration → Follower Agents → Backfill (owner
  // decision 2026-09-07); this page generates one scope and period.

  const reload = useCallback(async () => { await Promise.all([refetch(), refetchOptions()]) }, [refetch, refetchOptions])
  const activateRef = useRefreshOnActivate(reload)

  const openSet = (row: HighlightSetRecord) =>
    openFormInTab('period-highlights', row.highlight_sets__id, `${row.prog__scope_name ?? 'Highlights'} · ${row.prog__period_label}`)

  const handleGenerate = async (payload: GenerateHighlightsPayload) => {
    setGenerating(true)
    try {
      const result = await generateHighlights(payload, token())
      const status = result?.enqueue?.status
      toast.success(
        status === 'merged' ? 'Merged into the queued run'
          : status === 'deferred' ? 'Queued behind the current run for this scope'
            : 'Generation queued — live sets freeze into atoms, missing atoms run first, then the set lands'
      )
      setGenerateOpen(false)
      await refetch()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to start generation')
    } finally {
      setGenerating(false)
    }
  }

  // Finalise now (2026-09-07): a live set's key is generated on demand —
  // the members' live sets freeze into atoms without a model call, the
  // rest fan out, and the finalised set lands approved by policy.
  const finaliseNow = (row: HighlightSetRecord) => {
    modal.confirm({
      title: `Finalise ${row.prog__scope_name ?? 'this set'} · ${row.prog__period_label} now?`,
      content: 'The live set is finalised as it stands: companies with a live set are frozen into their atoms with no model call, companies without one get an atom run, then the finalised version lands (approved by policy, awaiting sign-off). The live set keeps accumulating for late news.',
      okText: 'Finalise now',
      onOk: () => handleGenerate(finalisePayload(row))
    })
  }

  const sortOrder = (key: string) => (sorting.key === key ? (sorting.direction === 'asc' ? 'ascend' : 'descend') : null)

  return (
    <div ref={activateRef} data-component='PeriodHighlightsPage' className='flex flex-1 min-h-0 flex-col overflow-hidden'>
      {contextHolder}
      <ViewToolbar
        moduleKey='period-highlights'
        recordTitleField='prog__scope_name'
        searchEnabled={false}
        addButtonEnabled={false}
        beforeAddSlot={
          <div className='flex items-center gap-2'>
            <Select
              size='small'
              style={{ minWidth: 150 }}
              placeholder='Any scope'
              allowClear
              value={filters.scopeType ?? undefined}
              onChange={(v) => { setPage(1); setFilters({ ...filters, scopeType: v ?? null }) }}
              options={options.highlight_sets__scope_type}
            />
            <Select
              size='small'
              mode='multiple'
              style={{ minWidth: 160 }}
              placeholder='Any status'
              allowClear
              maxTagCount='responsive'
              value={filters.statuses}
              onChange={(v) => { setPage(1); setFilters({ ...filters, statuses: v }) }}
              options={options.highlight_sets__status}
            />
            <Select
              size='small'
              mode='multiple'
              showSearch
              optionFilterProp='label'
              style={{ minWidth: 180 }}
              placeholder='Any fund'
              allowClear
              maxTagCount='responsive'
              value={filters.fundIds}
              onChange={(v) => { setPage(1); setFilters({ ...filters, fundIds: v }) }}
              onDropdownVisibleChange={(o) => o && refetchOptions()}
              options={options.highlight_sets__fund_id}
            />
            <Select
              size='small'
              style={{ minWidth: 130 }}
              placeholder='Any period'
              allowClear
              value={filters.periodEnd ?? undefined}
              onChange={(v) => { setPage(1); setFilters({ ...filters, periodEnd: v ?? null }) }}
              onDropdownVisibleChange={(o) => o && refetchOptions()}
              options={options.highlight_sets__period_end}
            />
            <span className='flex items-center gap-1.5 text-[11.5px] text-white/90'>
              Current only
              <Switch size='small' checked={filters.currentOnly} onChange={(v) => { setPage(1); setFilters({ ...filters, currentOnly: v }) }} />
            </span>
          </div>
        }
        beforeAddButtonSlot={
          <Button type='primary' size='small' className={RIBBON_CTA_CLASS} onClick={() => setGenerateOpen(true)}>
            Generate
          </Button>
        }
        onRefreshData={reload}
      />

      <div className='flex-1 min-h-0 overflow-auto p-2'>
        <FormSection title='Highlight sets' titleColor='blue' loading={loading} className='shadow-sm border border-gray-200'>
          <Table<HighlightSetRecord>
            size='small'
            rowKey='highlight_sets__id'
            dataSource={rows}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No highlight sets yet — the followers write a live set as the period unfolds; Generate finalises one for a scope and period.' }}
            pagination={{
              current: page, pageSize, total: count, showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100'],
              onChange: (p, ps) => { setPage(p); setPageSize(ps) }
            }}
            onChange={(_p, _f, sorter: any) => {
              if (sorter && sorter.columnKey && sorter.order) {
                setSorting({ key: String(sorter.columnKey), direction: sorter.order === 'ascend' ? 'asc' : 'desc' })
              }
            }}
            columns={[
              {
                title: 'Scope', key: 'prog__scope_name', dataIndex: 'prog__scope_name', sorter: true,
                sortOrder: sortOrder('prog__scope_name'),
                render: (v: string, r) => (
                  <span className='flex items-center gap-2'>
                    <button
                      type='button'
                      className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                      onClick={() => openSet(r)}
                    >
                      {v || '—'}
                    </button>
                    <span className='text-gray-400 text-xs'>{SCOPE_LABEL[r.highlight_sets__scope_type] ?? r.highlight_sets__scope_type}</span>
                  </span>
                )
              },
              {
                title: 'Period', key: 'highlight_sets__period_end', dataIndex: 'prog__period_label', sorter: true,
                sortOrder: sortOrder('highlight_sets__period_end'), width: 120
              },
              {
                title: 'Version', key: 'version', width: 120,
                render: (_: unknown, r) => { const t = versionTag(r); return <Tag color={t.color}>{t.label}</Tag> }
              },
              {
                title: 'Status', key: 'highlight_sets__status', dataIndex: 'highlight_sets__status', sorter: true,
                sortOrder: sortOrder('highlight_sets__status'), width: 110,
                render: (v: string) => { const t = STATUS_TAG[v] || { color: 'default', label: v }; return <Tag color={t.color}>{t.label}</Tag> }
              },
              {
                title: 'Items', key: 'items', width: 80, align: 'right' as const,
                render: (_: unknown, r) => (
                  <span>{r.prog__included_count}{r.prog__item_count !== r.prog__included_count ? <span className='text-gray-400'> / {r.prog__item_count}</span> : null}</span>
                )
              },
              {
                title: 'Coverage', key: 'coverage', width: 120,
                render: (_: unknown, r) => {
                  const cov = r.prog__coverage as CoverageNote | null
                  if (!cov || cov.expected == null) return <span className='text-gray-400'>—</span>
                  const missing = (cov.missing || []).length
                  if (r.highlight_sets__status === 'live') {
                    return <Tooltip title={`${cov.found} of ${cov.expected} portfolio companies have a live set or an atom for this period (${cov.live ?? 0} live); the rest have flagged nothing yet`}><Tag color='blue'>{cov.found}/{cov.expected}</Tag></Tooltip>
                  }
                  const frozen = Array.isArray(cov.frozen) ? cov.frozen.length : 0
                  return missing > 0
                    ? <Tooltip title={describeCoverageGaps(cov)}><Tag color='gold'>{cov.found}/{cov.expected}</Tag></Tooltip>
                    : <Tooltip title={frozen > 0 ? `${frozen} live set${frozen === 1 ? '' : 's'} frozen into atoms at finalise, no model call` : 'Every portfolio company had an approved atom'}><Tag color='green'>{cov.found}/{cov.expected}</Tag></Tooltip>
                }
              },
              {
                title: 'Generated', key: 'highlight_sets__generated_at', dataIndex: 'highlight_sets__generated_at', sorter: true,
                sortOrder: sortOrder('highlight_sets__generated_at'), width: 150,
                render: (v: string | null, r) => {
                  const run = r.agent_runs__status ? RUN_TAG[r.agent_runs__status] : null
                  return (
                    <span className='flex items-center gap-1.5'>
                      {v ? dayjs(v).format('MM/DD/YYYY') : '—'}
                      {run && run.label !== 'Generated' && <Tag color={run.color}>{run.label}</Tag>}
                    </span>
                  )
                }
              },
              {
                title: 'Approved by', key: 'approved_by', dataIndex: 'prog__approved_by_name', width: 150,
                render: (v: string | null, r) => v ? <span>{v}{r.highlight_sets__approved_at ? <span className='text-gray-400 text-xs'> {dayjs(r.highlight_sets__approved_at).format('MM/DD/YYYY')}</span> : null}</span> : <span className='text-gray-400'>—</span>
              },
              {
                // Sign-off (owner, 2026-09-07): whether a person read the approved set.
                title: 'Reviewed', key: 'review', width: 170,
                render: (_: unknown, r) => {
                  switch (r.prog__review_status) {
                    case 'signed_off':
                      return <Tooltip title={`Signed off${r.prog__signed_off_by_name ? ` by ${r.prog__signed_off_by_name}` : ''}${r.highlight_sets__signed_off_at ? ` on ${dayjs(r.highlight_sets__signed_off_at).format('MM/DD/YYYY')}` : ''}`}><Tag color='green'>Signed off</Tag></Tooltip>
                    case 'edited_since_sign_off':
                      return <Tooltip title='Edited after the sign-off — re-read and sign off again'><Tag color='gold'>Edited since sign-off</Tag></Tooltip>
                    case 'unreviewed':
                      return <Tooltip title='Approved by policy; nobody has read it yet — open it and press Sign off'><Tag color='gold'>Not reviewed</Tag></Tooltip>
                    default:
                      return <span className='text-gray-400'>—</span>
                  }
                }
              },
              {
                title: '', key: 'actions', width: 190, fixed: 'right' as const,
                render: (_: unknown, r) => r.highlight_sets__status === 'live'
                  ? (
                    <span className='flex items-center gap-1'>
                      <Button size='small' onClick={() => openSet(r)}>Open</Button>
                      <Button type='primary' size='small' disabled={generating} onClick={() => finaliseNow(r)}>Finalise now</Button>
                    </span>
                  )
                  : (r.highlight_sets__status === 'draft' || r.prog__review_status === 'unreviewed' || r.prog__review_status === 'edited_since_sign_off')
                    ? <Button type='primary' size='small' onClick={() => openSet(r)}>Review</Button>
                    : <Button size='small' onClick={() => openSet(r)}>Open</Button>
              }
            ]}
          />
        </FormSection>
      </div>

      <GenerateHighlightsModal
        open={generateOpen}
        submitting={generating}
        onCancel={() => setGenerateOpen(false)}
        onGenerate={handleGenerate}
      />
    </div>
  )
}

export default PeriodHighlightsPage
